import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ActorVideo } from 'src/api/actors/types';
import { Icon, Modal, Toast } from 'src/components';
import { Notification } from 'src/context/notification/types';
import { useSavedLearnables } from 'src/hooks/useSavedLearnables';
import { VideoActorFace } from '../learnableCards/utils';
import { LocalVideoCard } from './LocalVideoCard';
import { useActorProfile } from './useActorProfile';
import { useLocalProfileTracking } from './useLocalProfileTracking';
import * as S from './styles';

const msgs = defineMessages({
  back: { id: 'web_header_back' },
  error: { id: 'something_went_wrong' },
  handle: { id: 'local_profile_handle', defaultMessage: '@{name}' },
  videoCount: {
    id: 'local_profile_video_count',
    defaultMessage:
      '{count, plural, zero {<b>#</b> videos} one {<b>#</b> video} two {<b>#</b> videos} few {<b>#</b> videos} many {<b>#</b> videos} other {<b>#</b> videos}}',
  },
  bookmarkInfo: {
    id: 'local_profile_bookmark_info',
    defaultMessage: 'Bookmark a video to save the word or phrase in your list of words.',
  },
  saved: {
    id: 'blob_wordlist_detail_saved',
    defaultMessage: 'Added to Saved in My words',
  },
  removed: {
    id: 'blob_wordlist_detail_removed',
    defaultMessage: 'Removed from Saved in My words',
  },
  saveError: {
    id: 'blob_wordlist_browser_save_error',
    defaultMessage: 'We couldn’t update your saved words. Please try again.',
  },
});

const bold = (chunks: ReactNode[]) => <strong>{chunks}</strong>;

const oneVideoPerLearnable = (videos: ActorVideo[]) =>
  videos.filter(
    (video, index) => videos.findIndex(v => v.learnableId === video.learnableId) === index,
  );

export interface LocalProfileProps {
  isOpen: boolean;
  onClose: () => void;
  languagePairId: number;
  actor: VideoActorFace;
}

export const LocalProfile: FC<LocalProfileProps> = ({ isOpen, onClose, languagePairId, actor }) => {
  const intl = useIntl();
  const [playingVideo, setPlayingVideo] = useState<ActorVideo | null>(null);
  const [videosPlayed, setVideosPlayed] = useState(0);
  const [notification, setNotification] = useState<Notification | null>(null);
  const track = useLocalProfileTracking(languagePairId, actor.id);
  const wasOpenRef = useRef(false);
  const { isSaved, toggleSaved } = useSavedLearnables(isOpen ? languagePairId : null, {
    onSuccess: saved => {
      if (!isOpen) return;
      setNotification({
        status: 'success',
        text: intl.formatMessage(saved ? msgs.saved : msgs.removed),
      });
    },
    onError: () => {
      if (!isOpen) return;
      setNotification({ status: 'error', text: intl.formatMessage(msgs.saveError) });
    },
  });
  const { data: profile, isError } = useActorProfile(languagePairId, actor.id, isOpen);
  const videos = profile ? oneVideoPerLearnable(profile.videos) : [];

  const handle = actor.name ? intl.formatMessage(msgs.handle, { name: actor.name }) : null;

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      track('locals_profile_opened');
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, track]);

  useEffect(() => {
    if (!notification) return;
    const timeout = window.setTimeout(() => setNotification(null), 5000);
    return () => window.clearTimeout(timeout);
  }, [notification]);

  const playVideo = (video: ActorVideo) => {
    track('locals_profile_video_played', { learnable_id: String(video.learnableId) });
    setVideosPlayed(count => count + 1);
    setPlayingVideo(video);
  };

  const toggleVideoSaved = (video: ActorVideo) => {
    track('word_saved_from_profile', {
      learnable_id: String(video.learnableId),
      action_type: isSaved(video.learnableId) ? 'unsave' : 'save',
    });
    toggleSaved(video.learnableId, { type: 'localVideo', localName: actor.name });
  };

  const close = () => {
    track('locals_profile_closed', { videos_played: String(videosPlayed) });
    setPlayingVideo(null);
    setVideosPlayed(0);
    setNotification(null);
    onClose();
  };

  const renderVideos = () => {
    if (isError) {
      return <S.Message>{intl.formatMessage(msgs.error)}</S.Message>;
    }
    if (!profile) {
      return <S.ProfileLoader />;
    }
    return (
      <>
        <S.BookmarkInfo>
          <Icon name="bookmark" />
          {intl.formatMessage(msgs.bookmarkInfo)}
        </S.BookmarkInfo>
        <S.Grid>
          {videos.map(video => (
            <li key={video.url}>
              <LocalVideoCard
                video={video}
                onPlay={playVideo}
                isSaved={isSaved(video.learnableId)}
                onToggleSaved={toggleVideoSaved}
              />
            </li>
          ))}
        </S.Grid>
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={close}
      contentLabel={handle ?? undefined}
      backgroundColor="primary99"
      hideCloseButton
      fullSize
    >
      {playingVideo ? (
        <S.Player>
          <S.Header>
            <S.BackButton
              type="button"
              aria-label={intl.formatMessage(msgs.back)}
              onClick={() => setPlayingVideo(null)}
            >
              <Icon name="chevronLeft" />
            </S.BackButton>
          </S.Header>
          <S.PlayerVideo
            key={playingVideo.url}
            src={playingVideo.url}
            autoPlay
            controls
            playsInline
            disablePictureInPicture
            data-testid="local-video-player"
          />
          <S.PlayerCaption>
            <S.PlayerLearnable>{playingVideo.learnable}</S.PlayerLearnable>
            <S.PlayerTranslation>{playingVideo.translation}</S.PlayerTranslation>
          </S.PlayerCaption>
        </S.Player>
      ) : (
        <S.Content>
          <S.Header>
            <S.BackButton type="button" aria-label={intl.formatMessage(msgs.back)} onClick={close}>
              <Icon name="chevronLeft" />
            </S.BackButton>
            {handle && <S.HeaderTitle>{handle}</S.HeaderTitle>}
          </S.Header>
          <S.Profile>
            <S.ProfileFace facePictureSrc={actor.facePictureSrc} name={actor.name} />
            <div>
              {handle && <S.Name>{handle}</S.Name>}
              {profile && (
                <S.Stats>
                  {intl.formatMessage(msgs.videoCount, {
                    count: videos.length,
                    b: bold,
                  })}
                </S.Stats>
              )}
            </div>
          </S.Profile>
          {renderVideos()}
        </S.Content>
      )}
      {notification && (
        <S.BookmarkNotification
          role={notification.status === 'error' ? 'alert' : 'status'}
          aria-atomic="true"
        >
          <Toast status={notification.status} snackBar onClick={() => setNotification(null)}>
            {notification.text}
          </Toast>
        </S.BookmarkNotification>
      )}
    </Modal>
  );
};
